import { useState } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Send, CheckCircle, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }) };

export default function SuggestionsPage() {
  const [form, setForm] = useState({ name: "", email: "", category: "general", content: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.content) { toast.error("Please fill all required fields."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/suggestions`, form);
      setSubmitted(true);
    } catch { toast.error("Failed to submit."); } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div data-testid="suggestion-success" className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center max-w-lg px-6">
          <CheckCircle size={48} className="text-gold mx-auto mb-6" />
          <h2 className="font-heading text-3xl text-white mb-4">Thank You</h2>
          <p className="text-white/60 font-body font-light text-sm leading-relaxed mb-4">Your suggestion has been received and will be analyzed by our AI system to improve services, marketing, and the overall experience.</p>
          <p className="text-white/30 font-body text-xs mb-8">All feedback is processed securely. Your information is never sold to third parties.</p>
          <a href="/suggestions" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors">Submit Another</a>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="suggestions-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Suggestions</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">Help Us<br /><span className="gold-gradient-text">Improve</span></motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-base leading-relaxed">Your feedback shapes our future. Every suggestion is analyzed by our AI to identify trends, improve services, and enhance the experience for our entire community.</motion.p>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="p-4 bg-white/[0.02] border border-white/5 mb-8 flex items-start gap-3">
            <Shield size={16} className="text-gold flex-shrink-0 mt-0.5" />
            <p className="text-white/40 text-xs font-body leading-relaxed"><strong className="text-white/60">How we use your feedback:</strong> All suggestions are fed into our AI analytics system to identify patterns, improve event services, refine marketing strategies, and strengthen customer appreciation. <strong className="text-white/60">Your personal information is never sold to any third party without your explicit consent.</strong></p>
          </motion.div>

          <motion.form initial="hidden" animate="visible" onSubmit={handleSubmit} className="space-y-6" data-testid="suggestion-form">
            <motion.div variants={fadeUp} custom={4} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Your Name *</label>
                <Input data-testid="suggestion-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Email *</label>
                <Input data-testid="suggestion-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
              </div>
            </motion.div>
            <motion.div variants={fadeUp} custom={5}>
              <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Category</label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger data-testid="suggestion-category" className="bg-white/5 border-white/10 text-white h-12 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-luxury-charcoal border-white/10">
                  {["general", "website", "events", "services", "products", "customer service", "marketing"].map(c => <SelectItem key={c} value={c} className="text-white/80 capitalize text-sm">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </motion.div>
            <motion.div variants={fadeUp} custom={6}>
              <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Your Suggestion *</label>
              <Textarea data-testid="suggestion-content" value={form.content} onChange={e => setForm({...form, content: e.target.value})} placeholder="Share your idea for how we can improve..." rows={5} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm resize-none" />
            </motion.div>
            <motion.div variants={fadeUp} custom={7}>
              <button type="submit" disabled={loading} data-testid="submit-suggestion" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-10 py-4 hover:bg-gold-light transition-colors disabled:opacity-50">
                {loading ? "Submitting..." : "Submit Suggestion"} <Send size={16} />
              </button>
            </motion.div>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
